import React, { useState, useEffect, useRef } from 'react';
import './styles.css';
import supabase from '../../utils/supabaseClient';

function EditProductoPage({ 
  product, subfamilias, ivas, colores, tallas, onClose,
  onAddColor, onAddTalla, setColores, setTallas, user
}) {
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    detalle: '',
    sub_familia_id: '',
    precio1: '',
    precio2: '',
    precio3: '',
    descuento: 0,
    iva_id: '',
    color_numeric_id: '', // Changed from color_id to color_numeric_id
    talla_id: ''
  });
  
  // Add local state for dropdown data that can be refreshed
  const [localSubfamilias, setLocalSubfamilias] = useState(subfamilias);
  const [localIvas, setLocalIvas] = useState(ivas);
  const [localColores, setLocalColores] = useState(colores);
  const [localTallas, setLocalTallas] = useState(tallas);
  const [bodegas, setBodegas] = useState([]);
  
  // Add inventory state
  const [inventario, setInventario] = useState(null);
  const [inventarioLoading, setInventarioLoading] = useState(true);
  const [productImages, setProductImages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // New state variables
  const [selectedImages, setSelectedImages] = useState([]);
  const [availableImages, setAvailableImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const imagesPerPage = 6;

  // Add these state variables
  const [currentFolder, setCurrentFolder] = useState('highres');
  const [folderHistory, setFolderHistory] = useState(['highres']);

  // New state for color and talla management
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [newColorId, setNewColorId] = useState('');
  const [newColorCode, setNewColorCode] = useState('#000000');
  const [showTallaInput, setShowTallaInput] = useState(false);
  const [newTallaId, setNewTallaId] = useState('');
  const [colorSearch, setColorSearch] = useState('');
  const colorDropdownRef = useRef(null);

  // Get selected color object
  const selectedColor = localColores.find(c => c.numeric_id === form.color_numeric_id);

  // Filter colors based on search
  const filteredColores = localColores.filter(color => {
    if (!colorSearch.trim()) return true;
    const searchLower = colorSearch.toLowerCase();
    const colorName = color.name ? color.name.toLowerCase() : '';
    const colorRgb = color.rgb_code ? color.rgb_code.toLowerCase() : '';
    return colorName.includes(searchLower) || colorRgb.includes(searchLower);
  });

  // Update local state when props change
  useEffect(() => {
    setLocalSubfamilias(subfamilias);
    setLocalIvas(ivas);
    setLocalColores(colores);
    setLocalTallas(tallas);
  }, [subfamilias, ivas, colores, tallas]);

  // Fetch bodegas data on component mount
  useEffect(() => {
    async function fetchBodegas() {
      try {
        const { data: bodegasData, error } = await supabase.from('bodega').select('*');
        
        if (error) {
          console.error('Error fetching bodegas:', error);
          setMessage(`Error cargando bodegas: ${error.message}`);
        } else {
          setBodegas(bodegasData || []);
        }
      } catch (err) {
        console.error('Error in fetchBodegas:', err);
        setMessage(`Error cargando bodegas: ${err.message}`);
      }
    }
    
    fetchBodegas();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target)) {
        // Check if the clicked element is part of the color dropdown or search input
        const isColorDropdown = event.target.closest('.color-dropdown');
        const isColorSearchInput = event.target.classList.contains('color-search-input');
        
        if (!isColorDropdown && !isColorSearchInput) {
          setColorDropdownOpen(false);
        }
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize form with product data
  useEffect(() => {
    if (product) {
      setForm({
        codigo: product.codigo || '',
        nombre: product.nombre || '',
        detalle: product.detalle || '',
        sub_familia_id: product.sub_familia_id || '',
        precio1: product.precio1 || '',
        precio2: product.precio2 || '',
        precio3: product.precio3 || '',
        descuento: product.descuento || 0,
        iva_id: product.iva_id || '',
        color_numeric_id: product.color_numeric_id || '', // Changed from color_id
        talla_id: product.talla_id || ''
      });
      
      // Fetch inventory data for this product
      fetchInventario(product.id);
      // Fetch product images
      fetchProductImages(product.id);
    }
  }, [product]);

  // Fetch inventory data
  const fetchInventario = async (productId) => {
    setInventarioLoading(true);
    try {
      const { data, error } = await supabase
        .from('inventario')
        .select('*')
        .eq('producto_id', productId)
        .eq('bodega_id', 1); // Bodega 1 Taras - removed .single()
      
      if (error) {
        console.error('Error fetching inventory:', error);
        setMessage(`Error cargando inventario: ${error.message}`);
        setInventario(null);
      } else {
        // Handle the array response - take the first record if exists, or create default
        const inventoryRecord = data && data.length > 0 
          ? data[0] 
          : { producto_id: productId, bodega_id: 1, cantidad: 0 };
        setInventario(inventoryRecord);
      }
    } catch (err) {
      console.error('Error in fetchInventario:', err);
      setMessage(`Error cargando inventario: ${err.message}`);
      setInventario(null);
    } finally {
      setInventarioLoading(false);
    }
  };

  // Fetch product images
  const fetchProductImages = async (productId) => {
    try {
      const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('producto_id', productId);
      
      if (error) {
        console.error('Error fetching product images:', error);
        setMessage(`Error cargando imágenes: ${error.message}`);
      } else {
        setProductImages(data || []);
      }
    } catch (err) {
      console.error('Error in fetchProductImages:', err);
      setMessage(`Error cargando imágenes: ${err.message}`);
    }
  };

  // Refresh all data from database
  const refreshAllData = async () => {
    if (!product?.id) return;
    
    setRefreshing(true);
    try {
      // Refresh product-specific data
      await fetchInventario(product.id);
      await fetchProductImages(product.id);
      
      // Refresh dropdown data from database
      const [subfamiliasData, ivasData, coloresData, tallasData, bodegasData] = await Promise.all([
        supabase.from('subfamilia').select('*'),
        supabase.from('iva').select('*'),
        supabase.from('color').select('*'),
        supabase.from('talla').select('*'),
        supabase.from('bodega').select('*')
      ]);
      
      // Check for errors in dropdown data fetching
      const errors = [];
      if (subfamiliasData.error) errors.push(`Subfamilias: ${subfamiliasData.error.message}`);
      if (ivasData.error) errors.push(`IVAs: ${ivasData.error.message}`);
      if (coloresData.error) errors.push(`Colores: ${coloresData.error.message}`);
      if (tallasData.error) errors.push(`Tallas: ${tallasData.error.message}`);
      if (bodegasData.error) errors.push(`Bodegas: ${bodegasData.error.message}`);
      
      if (errors.length > 0) {
        setMessage(`Errores al actualizar: ${errors.join(', ')}`);
        return;
      }
      
      // Update local state with fresh data
      if (subfamiliasData.data) setLocalSubfamilias(subfamiliasData.data);
      if (ivasData.data) setLocalIvas(ivasData.data);
      if (coloresData.data) setLocalColores(coloresData.data);
      if (tallasData.data) setLocalTallas(tallasData.data);
      if (bodegasData.data) setBodegas(bodegasData.data);
      
      // Update parent component state via callbacks if available
      if (setColores && coloresData.data) {
        setColores(coloresData.data);
      }
      if (setTallas && tallasData.data) {
        setTallas(tallasData.data);
      }
      
      // Note: subfamilias, ivas, and bodegas are managed by parent component
      // The fresh data will be available if parent component re-fetches
      
      // Reset current folder to trigger re-fetch of available images
      const currentFolderValue = currentFolder;
      setCurrentFolder('');
      setTimeout(() => {
        setCurrentFolder(currentFolderValue);
      }, 100);
      
      setMessage('Datos actualizados correctamente');
    } catch (error) {
      console.error('Error refreshing data:', error);
      setMessage(`Error al actualizar los datos: ${error.message || error}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch available images from bucket
  useEffect(() => {
    const fetchAvailableImages = async () => {
      try {
        const { data: { publicUrl } } = supabase.storage
          .from('productsamples')
          .getPublicUrl('');
        
        const { data, error } = await supabase
          .storage
          .from('productsamples')
          .list(currentFolder, {
            sortBy: { column: 'name', order: 'asc' }
          });
        
        if (error) {
          console.error(`Error fetching from ${currentFolder}:`, error);
          return;
        }
        
        
        // Better folder detection
        const formattedItems = data.map(item => {
          // Check if item is likely a folder (has no extension or is explicitly marked)
          // The structure may vary between Supabase versions
          const isFolder = item.metadata === null || 
                          item.id === undefined ||
                          !item.name.includes('.');
          
          if (isFolder) {
            return {
              name: item.name,
              path: `${currentFolder}/${item.name}`,
              isFolder: true
            };
          } else {
            const path = `${currentFolder}/${item.name}`;
            return {
              name: item.name,
              path: path,
              url: `${publicUrl}${path}`,
              isFolder: false
            };
          }
        });
        
        // Sort folders first, then files
        const sortedItems = formattedItems.sort((a, b) => {
          // First sort by type (folders first)
          if (a.isFolder && !b.isFolder) return -1;
          if (!a.isFolder && b.isFolder) return 1;
          // Then sort by name
          return a.name.localeCompare(b.name);
        });
        
        setAvailableImages(sortedItems);
        
      } catch (err) {
        console.error('Error in fetchAvailableImages:', err);
      }
    };
    
    fetchAvailableImages();
  }, [currentFolder]); // Re-fetch when folder changes

  // Toggle image selection from bucket
  const toggleImageSelection = (imageUrl) => {
    const isAlreadySelected = selectedImages.includes(imageUrl);
    
    if (isAlreadySelected) {
      setSelectedImages(prev => prev.filter(url => url !== imageUrl));
    } else {
      setSelectedImages(prev => [...prev, imageUrl]);
    }
  };

  // Unlink an image from the product
  const unlinkImage = async (imageUrl) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('images')
        .delete()
        .eq('url', imageUrl)
        .eq('producto_id', product.id);
        
      if (error) {
        console.error('Error unlinking image:', error);
        setMessage(`Error: ${error.message}`);
      } else {
        setProductImages(prev => prev.filter(img => img.url !== imageUrl));
        setMessage('Imagen desvinculada correctamente');
      }
    } catch (err) {
      console.error('Error in unlinkImage:', err);
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para editar productos');
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      // Update product information
      const { error: productError } = await supabase
        .from('producto')
        .update({
          codigo: form.codigo,
          nombre: form.nombre,
          detalle: form.detalle || null,
          sub_familia_id: form.sub_familia_id ? parseInt(form.sub_familia_id, 10) : null,
          precio1: parseFloat(form.precio1),
          precio2: form.precio2 ? parseFloat(form.precio2) : null,
          precio3: form.precio3 ? parseFloat(form.precio3) : null,
          descuento: form.descuento ? parseFloat(form.descuento) : 0,
          iva_id: form.iva_id ? parseInt(form.iva_id, 10) : null,
          color_numeric_id: form.color_numeric_id || null, // Changed from color_id
          talla_id: form.talla_id || null
        })
        .eq('id', product.id);
      
      if (productError) throw productError;
      
      // Update or create inventory record
      if (inventario) {
        try {
          // Since we already have inventario data from fetchInventario, 
          // we don't need to check if it exists again
          const { error: inventoryError } = await supabase
            .from('inventario')
            .upsert(
              {
                producto_id: product.id,
                bodega_id: 1,
                cantidad: parseInt(inventario.cantidad, 10) || 0
              },
              {
                onConflict: 'producto_id,bodega_id',
                returning: 'minimal'
              }
            );
          
          if (inventoryError) throw inventoryError;
        } catch (inventoryError) {
          console.error('Error updating inventory:', inventoryError);
          throw inventoryError;
        }
      }
      
      // Link selected images
      for (const imageUrl of selectedImages) {
        // Check if the image is already linked to this product
        const isAlreadyLinked = productImages.some(img => img.url === imageUrl);
        
        if (!isAlreadyLinked) {
          const { error: imageError } = await supabase
            .from('images')
            .insert({
              url: imageUrl,
              producto_id: product.id
            });
          
          if (imageError) {
            console.error('Error linking image:', imageError);
            // Don't throw, just log the error and continue
          }
        }
      }
      
      setMessage('¡Producto actualizado con éxito!');
      setTimeout(() => onClose && onClose(), 1500);
    } catch (error) {
      console.error('Error updating product:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle inventory quantity change
  const handleInventarioChange = (e) => {
    const cantidad = parseInt(e.target.value, 10) || 0;
    setInventario(prev => ({ ...prev, cantidad }));
  };
  
  // Helpers for pagination
  const filteredImages = availableImages.filter(item => 
    item.name && item.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);

  const currentImages = filteredImages.slice(
    (currentPage - 1) * imagesPerPage, 
    currentPage * imagesPerPage
  );

  // Add a function to navigate to a subfolder
  const navigateToFolder = (folderPath) => {
    setFolderHistory(prev => [...prev, folderPath]);
    setCurrentFolder(folderPath);
    // Reset pagination when changing folders
    setCurrentPage(1);
  };

  // Add a function to go back to the previous folder
  const navigateBack = () => {
    if (folderHistory.length <= 1) return;
    
    const newHistory = [...folderHistory];
    newHistory.pop(); // Remove current folder
    const previousFolder = newHistory[newHistory.length - 1];
    
    setFolderHistory(newHistory);
    setCurrentFolder(previousFolder);
    // Reset pagination when changing folders
    setCurrentPage(1);
  };

  // Handle color selection
  const handleColorSelect = (colorNumericId) => {
    setForm({...form, color_numeric_id: colorNumericId});
    setColorDropdownOpen(false);
    setColorSearch(''); // Clear search when color is selected
  };

  // Function to add a new color
  async function handleAddColor(e) {
    e.preventDefault();
    
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para agregar colores');
      return;
    }
    
    if (!newColorId.trim()) {
      setMessage('El nombre del color no puede estar vacío');
      return;
    }
    
    const colorExists = localColores.some(color => color.name === newColorId.trim());
    if (colorExists) {
      setMessage('Este nombre de color ya existe');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('color')
        .insert([{ 
          name: newColorId.trim(), 
          rgb_code: newColorCode 
        }])
        .select('numeric_id, name, rgb_code');
      
      if (error) throw error;
      
      const newColor = data[0];
      // Update local state
      setLocalColores([...localColores, newColor]);
      // Update parent state if callback exists
      if (setColores) {
        setColores([...localColores, newColor]);
      }
      
      // Select the new color using its numeric_id
      setForm({...form, color_numeric_id: newColor.numeric_id});      setNewColorId('');
      setNewColorCode('#000000');
      setShowColorPicker(false);
      setMessage('Color agregado correctamente');
      
    } catch (error) {
      console.error('Error adding color:', error);
      setMessage(`Error al agregar el color: ${error.message}`);
    }
  }

  // Function to add a new talla
  async function handleAddTalla(e) {
    e.preventDefault();
    
    // Check authentication
    if (!user) {
      setMessage('Debes iniciar sesión para agregar tallas');
      return;
    }
    
    // Validate talla input
    if (!newTallaId.trim()) {
      setMessage('El ID de la talla no puede estar vacío');
      return;
    }
    
    // Check if talla ID already exists
    const tallaExists = localTallas.some(talla => talla.id === newTallaId.trim());
    if (tallaExists) {
      setMessage('Esta talla ya existe');
      return;
    }

    try {
      // Insert new talla into the database
      const { data, error } = await supabase
        .from('talla')
        .insert([{ id: newTallaId.trim() }]);
      
      if (error) throw error;
      
      // Update local state
      const newTalla = { id: newTallaId.trim() };
      setLocalTallas([...localTallas, newTalla]);
      // Update parent state if callback exists
      if (setTallas) {
        setTallas([...localTallas, newTalla]);
      }
      
      // Select the new talla in the form
      setForm({...form, talla_id: newTallaId.trim()});      // Reset talla input state
      setNewTallaId('');
      setShowTallaInput(false);
      setMessage('Talla agregada correctamente');
      
    } catch (error) {
      console.error('Error adding talla:', error);
      setMessage(`Error al agregar la talla: ${error.message}`);
    }
  }

  return (
    <div className="popup-content edit-popup">
      <div className="popup-header">
        <h2>Editar Producto</h2>
        <div className="header-actions">
          <button 
            type="button" 
            className="refresh-button" 
            onClick={refreshAllData}
            disabled={refreshing}
            title="Actualizar datos desde la base de datos"
          >
            {refreshing ? '🔄' : '↻'}
          </button>
          <button className="popup-close" onClick={onClose}>×</button>
        </div>
      </div>
      
      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Código:</label>
            <input
              type="text"
              value={form.codigo}
              onChange={(e) => setForm({ ...form, codigo: e.target.value })}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Nombre:</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Subfamilia:</label>
            <select
              value={form.sub_familia_id}
              onChange={(e) => setForm({ ...form, sub_familia_id: e.target.value })}
            >
              <option value="">Seleccionar...</option>
              {localSubfamilias.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.nombre}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label>Precio 1:</label>
            <input
              type="number"
              step="0.01"
              value={form.precio1}
              onChange={(e) => setForm({ ...form, precio1: e.target.value })}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Precio 2:</label>
            <input
              type="number"
              step="0.01"
              value={form.precio2}
              onChange={(e) => setForm({ ...form, precio2: e.target.value })}
            />
          </div>
          
          <div className="form-group">
            <label>Precio 3:</label>
            <input
              type="number"
              step="0.01"
              value={form.precio3}
              onChange={(e) => setForm({ ...form, precio3: e.target.value })}
            />
          </div>
          
          <div className="form-group">
            <label>Descuento (%):</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.descuento}
              onChange={(e) => setForm({ ...form, descuento: e.target.value })}
            />
          </div>
          
          <div className="form-group">
            <label>IVA:</label>
            <select
              value={form.iva_id}
              onChange={(e) => setForm({ ...form, iva_id: e.target.value })}
            >
              <option value="">Seleccionar...</option>
              {localIvas.map((iva) => (
                <option key={iva.id} value={iva.id}>
                  {(iva.porcentaje * 100).toFixed(0)}%
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label style={{ fontWeight: 500 }}>
              Color:
              <div className="color-select-container" ref={colorDropdownRef}>
                <div 
                  className="color-select-header"
                  onClick={() => setColorDropdownOpen(!colorDropdownOpen)}
                >
                  {form.color_numeric_id ? (
                    <>
                      <div 
                        className={`color-preview ${selectedColor?.name && selectedColor.name.toLowerCase() === 'colores' ? 'rainbow-gradient' : ''}`}
                        style={selectedColor?.name && selectedColor.name.toLowerCase() === 'colores' ? 
                          { display: 'block' } : 
                          { backgroundColor: selectedColor?.rgb_code || '#ccc', display: 'block' }
                        }
                      />
                      <div className="color-name">{selectedColor?.name}</div>
                    </>
                  ) : (
                    <div className="color-name">Seleccione color</div>
                  )}
                </div>
                
                {colorDropdownOpen && (
                  <div className="color-dropdown">
                    <div className="color-dropdown-header">Colores</div>
                    
                    {/* Color Search Bar */}
                    <div className="color-search-container">
                      <input
                        type="text"
                        placeholder="Buscar colores..."
                        value={colorSearch}
                        onChange={(e) => setColorSearch(e.target.value)}
                        className="color-search-input"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    
                    {filteredColores.length > 0 ? (
                      filteredColores.map(color => (
                        <div 
                          key={color.numeric_id}
                          className={`color-option ${form.color_numeric_id === color.numeric_id ? 'selected' : ''}`}
                          onClick={() => handleColorSelect(color.numeric_id)}
                          style={{ display: 'flex', width: '100%' }} // Force flex layout
                        >
                          <div className="color-option-box">
                            <div 
                              className={`color-option-preview ${color.name && color.name.toLowerCase() === 'colores' ? 'rainbow-gradient' : ''}`}
                              style={color.name && color.name.toLowerCase() === 'colores' ? {} : { backgroundColor: color.rgb_code || '#ccc' }}
                            />
                          </div>
                          <div className="color-option-name">
                            {color.name}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-colors-message">
                        No se encontraron colores que coincidan con la búsqueda.
                      </div>
                    )}
                    
                    <div 
                      className="add-color-option"
                      onClick={() => {
                        setColorDropdownOpen(false);
                        setShowColorPicker(true);
                      }}
                    >
                      <span className="add-icon">+</span>
                      <span>Agregar nuevo color</span>
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>
          
          <div className="form-group">
            <label style={{ fontWeight: 500 }}>
              Talla:
              <div className="talla-select-container">
                <select
                  value={form.talla_id}
                  onChange={e => setForm(f => ({ ...f, talla_id: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    marginTop: 4
                  }}
                >
                  <option value="">Seleccione talla</option>
                  {localTallas.map(t => (
                    <option key={t.id} value={t.id}>{t.id}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowTallaInput(true)}
                  style={{
                    marginTop: 8,
                    padding: '6px 10px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '13px'
                  }}
                >
                  <span style={{ marginRight: '4px', fontSize: '16px' }}>+</span> 
                  Agregar nueva talla
                </button>
              </div>
            </label>
          </div>
          
          <div className="form-group">
            <label>Detalle:</label>
            <textarea
              value={form.detalle}
              onChange={(e) => setForm({ ...form, detalle: e.target.value })}
              rows="3"
            ></textarea>
          </div>
        </div>
        
        {/* Inventory Section */}
        <div className="inventory-section">
          <h3>Inventario</h3>
          {inventarioLoading ? (
            <p>Cargando información de inventario...</p>
          ) : (
            <div className="inventory-form">
              <div className="form-group">
                <label>Bodega:</label>
                <input
                  type="text"
                  value="Bodega 1 Taras"
                  disabled
                  className="disabled-input"
                />
              </div>
              <div className="form-group">
                <label>Cantidad:</label>
                <input
                  type="number"
                  min="0"
                  value={inventario?.cantidad || 0}
                  onChange={handleInventarioChange}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Product Images Section */}
        <div className="product-images-section">
          <h3>Imágenes Actuales del Producto</h3>
          {productImages.length > 0 ? (
            <div className="product-images-grid">
              {productImages.map((image, index) => (
                <div key={index} className="product-image-item">
                  <img src={image.url} alt={`Product ${index+1}`} />
                  <button 
                    type="button"
                    className="image-unlink-button"
                    onClick={() => unlinkImage(image.url)}
                    disabled={loading}
                  >
                    <span className="unlink-icon">×</span>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p>Este producto no tiene imágenes.</p>
          )}
        </div>

        {/* Image Selection Section */}
        <div className="image-selection-section">
          <h3>Seleccionar Imágenes</h3>
          
          {/* Folder Navigation Bar */}
          <div className="folder-navigation">
            <button 
              type="button" 
              className="back-button" 
              onClick={navigateBack}
              disabled={folderHistory.length <= 1}
            >
              &laquo; Atrás
            </button>
            <span className="current-path">
              {currentFolder}
            </span>
          </div>
          
          <div className="image-search">
            <input
              type="text"
              placeholder="Buscar imágenes..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          
          <div className="available-images-grid">
            {currentImages.map((item, index) => (
              item.isFolder ? (
                // Render folder
                <div 
                  key={index}
                  className="available-folder-item"
                  onClick={() => navigateToFolder(item.path)}
                >
                  <div className="folder-icon">📁</div>
                  <span className="folder-name">{item.name}</span>
                </div>
              ) : (
                // Render image
                <div 
                  key={index}
                  className={`available-image-item ${
                    selectedImages.includes(item.url) ? 'selected' : ''
                  }`}
                  onClick={() => toggleImageSelection(item.url)}
                >
                  <img src={item.url} alt={`Available ${index+1}`} />
                  {selectedImages.includes(item.url) && (
                    <span className="selected-indicator">✓</span>
                  )}
                </div>
              )
            ))}
          </div>
          
          {totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                &laquo; Anterior
              </button>
              <span className="page-indicator">{currentPage} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Siguiente &raquo;
              </button>
            </div>
          )}
          
          {selectedImages.length > 0 && (
            <div className="selection-summary">
              {selectedImages.length} imagen(es) seleccionada(s).
            </div>
          )}
        </div>
        
        <div className="form-actions">
          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
          <button type="button" className="secondary-button" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
      
      {/* Add Color Picker Modal */}
      {showColorPicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 20,
            borderRadius: 8,
            width: '90%',
            maxWidth: 400,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>Agregar Nuevo Color</h3>
            
            <label style={{ display: 'block', marginBottom: 12 }}>
              Identificador del Color:
              <input
                type="text"
                value={newColorId}
                onChange={e => setNewColorId(e.target.value)}
                placeholder="Ej: ROJO, AZUL, VERDE"
                style={{
                  width: '100%',
                  padding: 8,
                  borderRadius: 6,
                  border: '1px solid #ccc',
                  marginTop: 4
                }}
              />
            </label>
            
            <label style={{ display: 'block', marginBottom: 12 }}>
              Código RGB:
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                <input
                  type="color"
                  value={newColorCode}
                  onChange={e => setNewColorCode(e.target.value)}
                  style={{
                    width: 40,
                    height: 40,
                    padding: 0,
                    border: 'none'
                  }}
                />
                <input
                  type="text"
                  value={newColorCode}
                  onChange={e => setNewColorCode(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #ccc'
                  }}
                />
              </div>
            </label>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button
                type="button"
                onClick={() => {
                  setShowColorPicker(false);
                  setNewColorId('');
                  setNewColorCode('#000000');
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  backgroundColor: '#f5f5f5',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleAddColor}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e60026',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
              >
                Agregar Color
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Talla Input Modal */}
      {showTallaInput && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            padding: 20,
            borderRadius: 8,
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            width: 400
          }}>
            <h3>Agregar Nueva Talla</h3>
            
            <div className="form-group">
              <label>ID de la Talla:</label>
              <input
                type="text"
                value={newTallaId}
                onChange={(e) => setNewTallaId(e.target.value)}
                required
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="button" 
                className="primary-button"
                onClick={handleAddTalla}
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Guardar Talla'}
              </button>
              <button 
                type="button" 
                className="secondary-button"
                onClick={() => setShowTallaInput(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditProductoPage;